import { useEffect } from 'react';
import { Navigate, Route, Routes as RouterRoutes, useNavigate } from 'react-router-dom';
import {
  activateAccountPath,
  forbiddenPath,
  notFoundPath,
  oauthAuthorizePath,
  resendActivationPath,
  resetPasswordLegacyTokenPath,
  resetPasswordPath,
  resetPasswordTokenPath,
  signInPath,
  signUpPath,
  ssoCallbackPath,
  ssoErrorPath,
} from 'common/config/urls';
import { AUTH_LOADING_LABELS, SIGN_IN_VIEW } from 'common/constants/auth';
import { FIRMS_SUMMARY_SLUG } from 'common/constants/firms';
import { CustomRouteElement } from 'components/CustomRoute/CustomRoute';
import { LoadingState } from 'components/ui';
import { useAuthSessionRestoreContext } from 'context';
import { DASHBOARD_COMPANY_SLUG, DASHBOARD_WORKBOARD_SLUG } from 'dashboard409a/common/constants';
import { DashboardLayout } from 'dashboard409a/layouts';
import { Dashboard as DashboardView } from 'dashboard409a/pages';
import AccountSettingsView from 'dashboard409a/pages/AccountSettings';
import AssignmentRulesView from 'dashboard409a/pages/AssignmentRules';
import WorkboardIndexRedirect from 'dashboard409a/pages/Workboard/WorkboardIndexRedirect';
import { AdminLayout, AuthLayout, FirmBaseLayout, TaskLayout } from 'layouts';
import { FIRM_WORKSPACE_ROUTE_PATHS } from 'layouts/FirmBaseLayout/FirmBaseLayout.routes';
import {
  ActivateAccount as ActivateAccountView,
  AuditLogs as AuditLogsView,
  CompanyContainer as CompanyContainerView,
  FirmDocuments as FirmDocumentsView,
  Firms as FirmsView,
  FirmValuations as FirmValuationsView,
  FirmWaterfall as FirmWaterfallView,
  Home as HomeView,
  Intelligence as IntelligenceView,
  NotFound as NotFoundView,
  ProcessManagement as ProcessManagementView,
  ResendActivation as ResendActivationView,
  ResetPasswordLegacyRedirect as ResetPasswordLegacyRedirectView,
  ResetPassword as ResetPasswordView,
  SetNewPassword as SetNewPasswordView,
  SignIn as SignInView,
  SignUp as SignUpView,
  SsoErrorView,
  SsoExchange as SsoExchangeView,
  User2FA as User2FAView,
  UserPermissions as UserPermissionsView,
  UserProfile as UserProfileView,
  UserSessions as UserSessionsView,
} from 'pages';
import { buildSignInUrl } from 'pages/Auth/utils';
import CommonCompanyProfilesPage from 'pages/CommonCompanyProfiles';
import { CompGroupsPage as CompGroupsView } from 'pages/Firms/pages/CompGroups';
import { DailyNavCompanies as DailyNavCompaniesView } from 'pages/Firms/pages/DailyNavCompanies';
import { DailyNavSettings as DailyNavSettingsView } from 'pages/Firms/pages/DailyNavSettings';
import { DailyNavSetup as DailyNavSetupView } from 'pages/Firms/pages/DailyNavSetup';
import { FeatureSettings as FeatureSettingsView } from 'pages/Firms/pages/FeatureSettings';
import { ScalarAiSettings as ScalarAiSettingsView } from 'pages/Firms/pages/ScalarAiSettings';
import { ScimConfiguration as ScimConfigurationView } from 'pages/Firms/pages/ScimConfiguration';
import { SingleSignOn as SingleSignOnView } from 'pages/Firms/pages/SingleSignOn';
import { VALUATIONS_DEFAULT_TERTIARY_NAVIGATION_SLUG } from 'pages/FirmValuations/firmValuationsNavigation.constants';
import FirmSummaryLegacyRedirect from 'pages/Intelligence/FirmSummaryLegacyRedirect';
import {
  INTELLIGENCE_DEFAULT_SECONDARY_NAVIGATION_SLUG,
  INTELLIGENCE_DEFAULT_TERTIARY_NAVIGATION_SLUG,
} from 'pages/Intelligence/intelligenceNavigation.constants';
import { OAuthAuthorize as OAuthAuthorizeView } from 'pages/OAuthAuthorize';

const RootRedirect = () => {
  const navigate = useNavigate();
  const { accessToken, isRestoringRememberedSession } = useAuthSessionRestoreContext();
  const hasStoredAccessToken = Boolean(accessToken);

  // Existing sessions should enter the workspace selector instead of seeing sign-in again.
  useEffect(() => {
    if (!hasStoredAccessToken || isRestoringRememberedSession) {
      return;
    }

    navigate(buildSignInUrl({ view: SIGN_IN_VIEW.WORKSPACE_SELECTION }), { replace: true });
  }, [hasStoredAccessToken, isRestoringRememberedSession, navigate]);

  if (isRestoringRememberedSession || hasStoredAccessToken) {
    return (
      <AuthLayout>
        <LoadingState label={AUTH_LOADING_LABELS.session} />
      </AuthLayout>
    );
  }

  return <Navigate to={signInPath} replace />;
};

const Routes = () => (
  <RouterRoutes>
    {/* Root redirect */}
    <Route path="/" element={<RootRedirect />} />

    {/* Auth */}
    <Route path={signInPath} element={<CustomRouteElement component={SignInView} layout={AuthLayout} />} />
    <Route path={signUpPath} element={<CustomRouteElement component={SignUpView} layout={AuthLayout} />} />
    <Route
      path={resetPasswordPath}
      element={<CustomRouteElement component={ResetPasswordView} layout={AuthLayout} />}
    />
    <Route
      path={resetPasswordTokenPath}
      element={<CustomRouteElement component={SetNewPasswordView} isPrivate={false} layout={AuthLayout} />}
    />
    <Route
      path={resetPasswordLegacyTokenPath}
      element={<CustomRouteElement component={ResetPasswordLegacyRedirectView} isPrivate={false} />}
    />
    <Route
      path={activateAccountPath}
      element={<CustomRouteElement component={ActivateAccountView} layout={AuthLayout} />}
    />
    <Route
      path={resendActivationPath}
      element={<CustomRouteElement component={ResendActivationView} layout={AuthLayout} />}
    />

    {/* SSO and OAuth */}
    <Route path={ssoErrorPath} element={<CustomRouteElement component={SsoErrorView} layout={AuthLayout} />} />
    <Route path={ssoCallbackPath} element={<SsoExchangeView />} />
    <Route path={oauthAuthorizePath} element={<OAuthAuthorizeView />} />

    {/* Keep the layout component stable so query-param edits do not remount the firm workspace. */}
    {/* Firm workspace route paths below are relative to /firms/:firmSlugParam. */}
    <Route
      path={FIRM_WORKSPACE_ROUTE_PATHS.BASE}
      element={<CustomRouteElement isPrivate layout={FirmBaseLayout} />}>
      {/* /firms/:firmSlugParam -> /firms/:firmSlugParam/home */}
      <Route index element={<Navigate to={FIRM_WORKSPACE_ROUTE_PATHS.HOME} replace />} />

      {/* /firms/:firmSlugParam/home */}
      <Route
        path={FIRM_WORKSPACE_ROUTE_PATHS.HOME}
        element={<CustomRouteElement component={HomeView} />}
      />

      {/* /firms/:firmSlugParam/intelligence -> /firms/:firmSlugParam/intelligence/summaries/firm-summary */}
      <Route
        path={FIRM_WORKSPACE_ROUTE_PATHS.INTELLIGENCE}
        element={
          <Navigate
            to={`${INTELLIGENCE_DEFAULT_SECONDARY_NAVIGATION_SLUG}/${INTELLIGENCE_DEFAULT_TERTIARY_NAVIGATION_SLUG}`}
            replace
          />
        }
      />

      {/* /firms/:firmSlugParam/intelligence/:secondaryTabSlugParam/:tertiaryTabSlugParam? */}
      <Route
        path={FIRM_WORKSPACE_ROUTE_PATHS.INTELLIGENCE_TAB}
        element={<CustomRouteElement component={IntelligenceView} />}
      />

      {/* Legacy /firms/:firmSlugParam/summary -> /firms/:firmSlugParam/intelligence/firm-summary */}
      <Route path={FIRMS_SUMMARY_SLUG} element={<FirmSummaryLegacyRedirect />} />

      {/* /firms/:firmSlugParam/valuations -> /firms/:firmSlugParam/valuations/firm-valuations */}
      <Route
        path={FIRM_WORKSPACE_ROUTE_PATHS.VALUATIONS}
        element={<Navigate to={VALUATIONS_DEFAULT_TERTIARY_NAVIGATION_SLUG} replace />}
      />

      {/* /firms/:firmSlugParam/valuations/:tertiaryTabSlugParam/:tertiaryViewSlugParam? */}
      <Route
        path={FIRM_WORKSPACE_ROUTE_PATHS.VALUATIONS_TERTIARY}
        element={<CustomRouteElement component={FirmValuationsView} />}
      />

      {/* /firms/:firmSlugParam/waterfalls */}
      <Route
        path={FIRM_WORKSPACE_ROUTE_PATHS.WATERFALLS}
        element={<CustomRouteElement component={FirmWaterfallView} />}
      />

      {/* /firms/:firmSlugParam/documents */}
      <Route
        path={FIRM_WORKSPACE_ROUTE_PATHS.DOCUMENTS}
        element={<CustomRouteElement component={FirmDocumentsView} />}
      />

      {/* /firms/:firmSlugParam/documents/:documentsTabSlugParam */}
      <Route
        path={FIRM_WORKSPACE_ROUTE_PATHS.DOCUMENTS_TAB}
        element={<CustomRouteElement component={FirmDocumentsView} />}
      />

      {/* /firms/:firmSlugParam/audit-logs */}
      <Route path={FIRM_WORKSPACE_ROUTE_PATHS.AUDIT_LOGS} element={<CustomRouteElement component={AuditLogsView} />} />

      {/* /firms/:firmSlugParam/users/account-settings */}
      <Route path="users/account-settings" element={<CustomRouteElement component={UserProfileView} />} />

      {/* /firms/:firmSlugParam/users/:userIdParam/account-settings */}
      <Route
        path={FIRM_WORKSPACE_ROUTE_PATHS.USER_DETAIL_ACCOUNT_SETTINGS}
        element={<CustomRouteElement component={UserProfileView} />}
      />

      {/* /firms/:firmSlugParam/users/two-factor-authentication */}
      <Route path="users/two-factor-authentication" element={<CustomRouteElement component={User2FAView} />} />

      {/* /firms/:firmSlugParam/users/active-sessions */}
      <Route path="users/active-sessions" element={<CustomRouteElement component={UserSessionsView} />} />

      {/* /firms/:firmSlugParam/comp-groups */}
      <Route
        path={FIRM_WORKSPACE_ROUTE_PATHS.COMP_GROUPS}
        element={<CustomRouteElement component={CompGroupsView} />}
      />

      {/* /firms/:firmSlugParam/sso */}
      <Route
        path={FIRM_WORKSPACE_ROUTE_PATHS.SINGLE_SIGN_ON}
        element={<CustomRouteElement component={SingleSignOnView} />}
      />

      {/* /firms/:firmSlugParam/scim */}
      <Route
        path={FIRM_WORKSPACE_ROUTE_PATHS.SCIM_CONFIGURATION}
        element={<CustomRouteElement component={ScimConfigurationView} />}
      />

      {/* /firms/:firmSlugParam/daily-nav-settings */}
      <Route
        path={FIRM_WORKSPACE_ROUTE_PATHS.DAILY_NAV_SETTINGS}
        element={<CustomRouteElement component={DailyNavSettingsView} />}
      />

      {/* /firms/:firmSlugParam/daily-nav-companies */}
      <Route
        path={FIRM_WORKSPACE_ROUTE_PATHS.DAILY_NAV_COMPANIES}
        element={<CustomRouteElement component={DailyNavCompaniesView} />}
      />

      {/* /firms/:firmSlugParam/daily-nav-setup */}
      <Route
        path={FIRM_WORKSPACE_ROUTE_PATHS.DAILY_NAV_SETUP}
        element={<CustomRouteElement component={DailyNavSetupView} />}
      />

      {/* /firms/:firmSlugParam/feature-settings */}
      <Route
        path={FIRM_WORKSPACE_ROUTE_PATHS.FEATURE_SETTINGS}
        element={<CustomRouteElement component={FeatureSettingsView} />}
      />

      {/* /firms/:firmSlugParam/scalar-ai */}
      <Route
        path={FIRM_WORKSPACE_ROUTE_PATHS.SCALAR_AI_SETTINGS}
        element={<CustomRouteElement component={ScalarAiSettingsView} />}
      />

      {/* /firms/:firmSlugParam/user-management */}
      <Route
        path={FIRM_WORKSPACE_ROUTE_PATHS.USER_MANAGEMENT}
        element={<CustomRouteElement component={UserPermissionsView} />}
      />

      {/* /firms/:firmSlugParam/companies/:companySlugParam/:tableSlugParam */}
      <Route
        path={FIRM_WORKSPACE_ROUTE_PATHS.COMPANY_TAB}
        element={<CustomRouteElement component={CompanyContainerView} />}
      />

      {/* /firms/:firmSlugParam/:tableSlugParam */}
      <Route path={FIRM_WORKSPACE_ROUTE_PATHS.FIRM_TAB} element={<CustomRouteElement component={FirmsView} />} />
    </Route>

    {/* 409A Dashboard */}
    <Route
      path={DASHBOARD_COMPANY_SLUG}
      element={<CustomRouteElement isPrivate layout={props => <DashboardLayout {...props} />} />}>
      <Route path=":tableSlugParam" element={<CustomRouteElement component={DashboardView} />} />
      <Route path=":tableSlugParam/:valuationId" element={<CustomRouteElement component={DashboardView} />} />
    </Route>

    {/* Workboard */}
    <Route
      path={DASHBOARD_WORKBOARD_SLUG}
      element={<CustomRouteElement isPrivate layout={props => <DashboardLayout {...props} />} />}>
      <Route index element={<CustomRouteElement component={WorkboardIndexRedirect} />} />
      <Route path="account-settings" element={<CustomRouteElement component={AccountSettingsView} />} />
      <Route path="assignment-rules" element={<CustomRouteElement component={AssignmentRulesView} />} />
      <Route path=":tabSlugParam" element={<CustomRouteElement component={DashboardView} />} />
    </Route>

    {/* Process Management */}
    <Route
      path="/:processType/requests/:requestId/:hmacToken/:portal?"
      element={<CustomRouteElement component={ProcessManagementView} layout={TaskLayout} />}
    />

    {/* Admin */}
    <Route
      path="/admin/common-company-profiles"
      element={
        <CustomRouteElement
          component={CommonCompanyProfilesPage}
          isPrivate
          layout={props => <AdminLayout {...props} />}
        />
      }
    />

    {/* Shared error routes */}
    <Route path={notFoundPath} element={<CustomRouteElement component={NotFoundView} />} />
    <Route path={forbiddenPath} element={<CustomRouteElement component={NotFoundView} />} />

    {/* Fallback */}
    <Route path="*" element={<Navigate to={notFoundPath} replace />} />
  </RouterRoutes>
);

export default Routes;
